import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { createApiFetch } from "../auth/apiFetch";
import { buildApiUrl } from "../runtimeConfig";
import { useAuth } from "./AuthContext";

export type CountryCode = 
  | "austria" | "belgium" | "bulgaria" | "croatia" | "cyprus"
  | "czechia" | "denmark" | "estonia" | "finland" | "france"
  | "germany" | "greece" | "hungary" | "ireland" | "italy"
  | "latvia" | "lithuania" | "luxembourg" | "malta" | "netherlands"
  | "poland" | "portugal" | "romania" | "slovakia" | "slovenia"
  | "spain" | "sweden";

interface RegionalSettings {
  country: CountryCode;
  language: string;
  timezone: string;
  currency: string;
}

interface RegionalSettingsContextType {
  settings: RegionalSettings;
  refresh: () => Promise<void>;
  updateCountry: (country: CountryCode) => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
  updateTimezone: (timezone: string) => Promise<void>;
  updateCurrency: (currency: string) => Promise<void>;
}

const RegionalSettingsContext = createContext<RegionalSettingsContextType | undefined>(undefined);

type RegionalSettingsResponse = {
  user?: {
    language?: string | null;
    timezone?: string | null;
  };
  organization?: {
    country?: string | null;
    currency?: string | null;
  };
};

const ISO_TO_COUNTRY: Record<string, CountryCode> = {
  AT: "austria",
  BE: "belgium",
  BG: "bulgaria",
  HR: "croatia",
  CY: "cyprus",
  CZ: "czechia",
  DK: "denmark",
  EE: "estonia",
  FI: "finland",
  FR: "france",
  DE: "germany",
  GR: "greece",
  HU: "hungary",
  IE: "ireland",
  IT: "italy",
  LV: "latvia",
  LT: "lithuania",
  LU: "luxembourg",
  MT: "malta",
  NL: "netherlands",
  PL: "poland",
  PT: "portugal",
  RO: "romania",
  SK: "slovakia",
  SI: "slovenia",
  ES: "spain",
  SE: "sweden",
};

const COUNTRY_TO_ISO: Record<CountryCode, string> = {
  austria: "AT",
  belgium: "BE",
  bulgaria: "BG",
  croatia: "HR",
  cyprus: "CY",
  czechia: "CZ",
  denmark: "DK",
  estonia: "EE",
  finland: "FI",
  france: "FR",
  germany: "DE",
  greece: "GR",
  hungary: "HU",
  ireland: "IE",
  italy: "IT",
  latvia: "LV",
  lithuania: "LT",
  luxembourg: "LU",
  malta: "MT",
  netherlands: "NL",
  poland: "PL",
  portugal: "PT",
  romania: "RO",
  slovakia: "SK",
  slovenia: "SI",
  spain: "ES",
  sweden: "SE",
};

const DEFAULT_SETTINGS: RegionalSettings = {
  country: "portugal",
  language: "en",
  timezone: "europe-lisbon",
  currency: "eur",
};

export const toIsoCountryCode = (country?: CountryCode | null): string => {
  if (!country) {
    return COUNTRY_TO_ISO[DEFAULT_SETTINGS.country];
  }

  return COUNTRY_TO_ISO[country] ?? COUNTRY_TO_ISO[DEFAULT_SETTINGS.country];
};

const normalizeCountry = (isoCode?: string | null): CountryCode => {
  if (!isoCode) {
    return DEFAULT_SETTINGS.country;
  }

  const normalized = isoCode.trim().toUpperCase();
  return ISO_TO_COUNTRY[normalized] ?? DEFAULT_SETTINGS.country;
};

const normalizeCurrency = (currency?: string | null): string => {
  if (!currency) {
    return DEFAULT_SETTINGS.currency;
  }

  return currency.trim().toLowerCase();
};

export function RegionalSettingsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<RegionalSettings>(DEFAULT_SETTINGS);
  const apiFetch = useMemo(() => createApiFetch(8000), []);

  const applyPayload = useCallback((payload: RegionalSettingsResponse) => {
    setSettings((current) => ({
      country: normalizeCountry(payload.organization?.country),
      currency: normalizeCurrency(payload.organization?.currency),
      language: payload.user?.language?.trim().toLowerCase() || current.language,
      timezone: payload.user?.timezone?.trim() || current.timezone,
    }));
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setSettings(DEFAULT_SETTINGS);
      return;
    }

    try {
      const response = await apiFetch(buildApiUrl("/api/settings/regional"), {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to load regional settings.");
      }

      const payload = await response.json() as RegionalSettingsResponse;
      applyPayload(payload);
    } catch (error) {
      console.error("Failed to load regional settings.", error);
    }
  }, [apiFetch, applyPayload, isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateCountry = useCallback(
    async (country: CountryCode) => {
      const isoCountry = COUNTRY_TO_ISO[country];
      const response = await apiFetch(buildApiUrl("/api/settings/regional/organization"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          country: isoCountry,
          currency: settings.currency.toUpperCase(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update organization settings.");
      }

      setSettings((prev) => ({ ...prev, country }));
    },
    [apiFetch, settings.currency],
  );

  const updateCurrency = useCallback(
    async (currency: string) => {
      const response = await apiFetch(buildApiUrl("/api/settings/regional/organization"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          country: COUNTRY_TO_ISO[settings.country],
          currency: currency.toUpperCase(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update organization settings.");
      }

      setSettings((prev) => ({ ...prev, currency }));
    },
    [apiFetch, settings.country],
  );

  const updateLanguage = useCallback(
    async (language: string) => {
      const response = await apiFetch(buildApiUrl("/api/settings/regional/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          language: language.toLowerCase(),
          timezone: settings.timezone,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update user settings.");
      }

      setSettings((prev) => ({ ...prev, language }));
    },
    [apiFetch, settings.timezone],
  );

  const updateTimezone = useCallback(
    async (timezone: string) => {
      const response = await apiFetch(buildApiUrl("/api/settings/regional/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          language: settings.language,
          timezone,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update user settings.");
      }

      setSettings((prev) => ({ ...prev, timezone }));
    },
    [apiFetch, settings.language],
  );

  return (
    <RegionalSettingsContext.Provider 
      value={{ 
        settings,
        refresh,
        updateCountry,
        updateLanguage,
        updateTimezone,
        updateCurrency 
      }}
    >
      {children}
    </RegionalSettingsContext.Provider>
  );
}

export function useRegionalSettings() {
  const context = useContext(RegionalSettingsContext);
  if (context === undefined) {
    throw new Error("useRegionalSettings must be used within a RegionalSettingsProvider");
  }
  return context;
}
